import { useEffect, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Building2, FileText, Plus, Upload, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FacilitiesSection = () => {
  const [facilities, setFacilities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", location: "", contact: "" });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/facilities").then(({ data }) => setFacilities(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.info("Please add a facility name."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("location", form.location);
      fd.append("contact", form.contact);
      files.forEach((f) => fd.append("files", f));
      await api.post("/facilities", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ name: "", description: "", location: "", contact: "" });
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      setShowForm(false);
      await load();
      toast.success("Facility submitted with your documents.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const token = localStorage.getItem("aurea_token");

  return (
    <div className="space-y-5" data-testid="facilities-section">
      <div className="flex items-start justify-between gap-3">
        <p className="text-ink-2 leading-relaxed max-w-2xl">Treatment facilities can list their programs and upload their curriculum, rules, and intake paperwork for people seeking care.</p>
        <Button onClick={() => setShowForm(!showForm)} className="rounded-full bg-brand hover:bg-brand-hover text-linen shrink-0" data-testid="add-facility-toggle"><Plus className="w-4 h-4 mr-1" /> Add facility</Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-3xl border border-linen-3 p-6 space-y-3" data-testid="facility-form">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Facility name" className="rounded-2xl bg-linen border-linen-3 h-12" data-testid="facility-name" />
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Programs, approach, levels of care…" className="rounded-2xl bg-linen border-linen-3 min-h-[90px]" data-testid="facility-desc" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (city, state)" className="rounded-2xl bg-linen border-linen-3 h-12" data-testid="facility-location" />
            <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Contact (phone or email)" className="rounded-2xl bg-linen border-linen-3 h-12" data-testid="facility-contact" />
          </div>
          <div>
            <label className="text-sm text-ink-2 flex items-center gap-1.5 mb-1"><Upload className="w-4 h-4" /> Curriculum, rules & paperwork (PDF, DOC, images)</label>
            <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" onChange={(e) => setFiles(Array.from(e.target.files))} className="block w-full text-sm text-ink-2 file:mr-3 file:rounded-full file:border-0 file:bg-brand file:text-linen file:px-4 file:py-2 file:text-sm" data-testid="facility-files" />
            {files.length > 0 && <p className="text-xs text-ink-mute mt-1">{files.length} file(s) selected</p>}
          </div>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover text-linen" data-testid="facility-submit">{saving ? "Uploading…" : "Submit facility"}</Button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {facilities.map((f) => (
          <div key={f.id} className="bg-white rounded-3xl border border-linen-3 p-6" data-testid="facility-card">
            <div className="flex items-center gap-2"><Building2 className="w-5 h-5 text-brand" /><h3 className="font-serif text-2xl text-ink">{f.name}</h3></div>
            {f.description && <p className="text-sm text-ink-2 mt-2 leading-relaxed">{f.description}</p>}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-ink-mute">
              {f.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{f.location}</span>}
              {f.contact && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{f.contact}</span>}
            </div>
            {f.files?.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {f.files.map((doc) => (
                  <a key={doc.id} href={`${BACKEND}/api/files/${doc.path}?auth=${token}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-accent hover:underline" data-testid="facility-file">
                    <FileText className="w-4 h-4" /> {doc.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {facilities.length === 0 && <p className="text-sm text-ink-mute">No facilities listed yet.</p>}
      </div>
    </div>
  );
};

export default FacilitiesSection;
